// src/features/works/components/AssignEquipmentModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, InputGroup, Row, Col, ListGroup, Spinner, Badge } from 'react-bootstrap';
import { FaSearch, FaPlus, FaTimes, FaSave } from 'react-icons/fa';

// Servicios
import ToolsService from '../../tools/ToolsService';
import WorksService from '../WorksService';
import CustomSnackbar from '../../../components/CustomSnackbar';
import { TIPOS_EQUIPOS, getTipoDescripcion } from '../../../utils/constants';


/**
 * Modal para asignar equipos a una obra
 */
const AssignEquipmentModal = ({ show, onHide, workId, onEquipmentsAssigned }) => {
  // ✅ Estados principales con control de loading
  const [loading, setLoading] = useState(true);
  const [processingAssignment, setProcessingAssignment] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  
  // ✅ useRef para prevenir múltiples operaciones
  const assignmentRef = useRef(false);
  const mountedRef = useRef(true);
  
  // Estados de datos
  const [equipmentsInStock, setEquipmentsInStock] = useState([]);
  const [filteredEquipments, setFilteredEquipments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [equipmentsToAssign, setEquipmentsToAssign] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [equipmentQuantity, setEquipmentQuantity] = useState(1);
  const [serialNumber, setSerialNumber] = useState('');
  const [serials, setSerials] = useState([]);

  // ✅ Control de montaje
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Cargar equipos disponibles al abrir el modal
  useEffect(() => {
    if (show && mountedRef.current) {
      loadEquipmentsInStock();
    }
  }, [show]);

  // Filtrar equipos cuando cambia la búsqueda o los filtros
  useEffect(() => {
    filterEquipments();
  }, [searchTerm, activeFilters, equipmentsInStock]);

  // ✅ Función para cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Cargar equipos en stock desde la API
  const loadEquipmentsInStock = async () => {
    try {
      setLoading(true);
      //console.log('🔄 Cargando Herramientas disponibles para obra...');
      
      const toolsData = await ToolsService.getAllTools();
      
      // Filtrar solo los equipos que tienen stock disponible
      const availableTools = toolsData.filter(tool => {
        const availableStock = tool.quantity - (tool.assigned || 0);
        return availableStock > 0;
      });
      
      if (mountedRef.current) {
        setEquipmentsInStock(availableTools);
        setFilteredEquipments(availableTools);
        //console.log(`✅ ${availableTools.length} Herramientas disponibles cargados para obra`);
      }
    } catch (error) {
      console.error('❌ Error al cargar Herramientas:', error);
      if (mountedRef.current) {
        setSnackbar({
          open: true,
          type: 'error',
          message: 'Error al cargar la lista de Herramientas disponibles'
        });
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Filtrar equipos según término de búsqueda y filtros activos
  const filterEquipments = () => {
    let filtered = [...equipmentsInStock];
    
    // Filtrar por término de búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(equip => {
        const name = (equip.name || '').toLowerCase();
        const typeDesc = getTipoDescripcion(equip.type, TIPOS_EQUIPOS).toLowerCase();
        return name.includes(term) || typeDesc.includes(term);
      });
    }
    
    // Filtrar por tipos de equipo activos
    const activeTypeFilters = Object.keys(activeFilters).filter(key => activeFilters[key]);
    if (activeTypeFilters.length > 0) {
      filtered = filtered.filter(equip => activeTypeFilters.includes(equip.type));
    }
    
    setFilteredEquipments(filtered);
  };

  // Manejar la selección de un equipo
  const handleSelectEquipment = (equipment) => {
    setSelectedEquipment(equipment);
    setEquipmentQuantity(1);
    setSerialNumber('');
    setSerials([]);
  };

  // Manejar cambio en los filtros por tipo
  const handleFilterChange = (type) => {
    setActiveFilters(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Calcular stock disponible
  const getAvailableStock = (equipment) => {
    if (!equipment) return 0;
    
    // Stock total
    const totalStock = equipment.quantity || 0;
    
    // Asignadas según API
    const assignedFromAPI = equipment.assigned || 0;
    
    // Ya seleccionadas para asignar en este formulario
    const currentlySelected = equipmentsToAssign
      .filter(item => item.id === equipment.id)
      .reduce((total, item) => total + (parseInt(item.quantity) || 1), 0);
    
    return totalStock - assignedFromAPI - currentlySelected;
  };

  // ✅ CORREGIDO: Añadir serial con límite basado en cantidad
  const handleAddSerial = () => {
    if (!serialNumber.trim()) {
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Por favor ingrese un número de serie'
      });
      return;
    }
    
    // ✅ VERIFICAR LÍMITE: No más seriales que cantidad de equipos
    if (serials.length >= equipmentQuantity) {
      setSnackbar({
        open: true,
        type: 'error',
        message: `No puede agregar más de ${equipmentQuantity} serial(es) para esta cantidad de Herramientas`
      });
      return;
    }
    
    // Verificar si ya existe
    if (serials.includes(serialNumber.trim())) {
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Este serial ya fue agregado'
      });
      return;
    }
    
    // Añadir el serial y limpiar input
    setSerials([...serials, serialNumber.trim()]);
    setSerialNumber('');
    //console.log(`✅ Serial agregado: ${serialNumber.trim()} (${serials.length + 1}/${equipmentQuantity})`);
  };

  // Quitar serial de la lista
  const handleRemoveSerial = (index) => {
    const updatedSerials = [...serials];
    const removedSerial = updatedSerials.splice(index, 1)[0];
    setSerials(updatedSerials);
    //console.log(`🗑️ Serial removido: ${removedSerial}`);
  };

  // ✅ CORREGIDO: Actualizar serials cuando cambia la cantidad
  const handleQuantityChange = (newQuantity) => {
    const quantity = parseInt(newQuantity) || 1;
    setEquipmentQuantity(quantity);
    
    // ✅ Si la nueva cantidad es menor que la cantidad de serials, recortar
    if (serials.length > quantity) {
      const trimmedSerials = serials.slice(0, quantity);
      setSerials(trimmedSerials);
      //console.log(`⚠️ Cantidad reducida: conservando solo ${quantity} serials de ${serials.length}`);
      
      setSnackbar({
        open: true,
        type: 'warning',
        message: `Cantidad reducida: se conservaron solo ${quantity} serials`
      });
    }
  };

  // Añadir equipo a la lista de equipos a asignar
  const handleAddToAssignList = () => {
    if (!selectedEquipment) return;
    
    // Verificar stock disponible
    const availableStock = getAvailableStock(selectedEquipment);
    if (equipmentQuantity > availableStock) {
      setSnackbar({
        open: true,
        type: 'error',
        message: `No hay suficiente stock disponible. Stock disponible: ${availableStock}`
      });
      return;
    }
    
    // ✅ Advertir si no hay suficientes serials (opcional)
    if (serials.length > 0 && serials.length < equipmentQuantity) {
      //console.log(`⚠️ Solo ${serials.length} serials para ${equipmentQuantity} Herramientas`);
    }
    
    // Crear objeto para asignar
    const equipmentToAssign = {
      id: selectedEquipment.id,
      name: selectedEquipment.name,
      type: selectedEquipment.type,
      quantity: equipmentQuantity,
      serial: [...serials] // Copia de serials
    };
    
    // Agregar a la lista
    setEquipmentsToAssign([...equipmentsToAssign, equipmentToAssign]);
    
    //console.log(`✅ Equipo agregado a lista:`, equipmentToAssign);
    
    // Limpiar selección
    setSelectedEquipment(null);
    setEquipmentQuantity(1);
    setSerialNumber('');
    setSerials([]);
  };

  // Quitar equipo de la lista de asignación
  const handleRemoveFromAssignList = (index) => {
    const updatedEquipments = [...equipmentsToAssign];
    const removedEquipment = updatedEquipments.splice(index, 1)[0];
    setEquipmentsToAssign(updatedEquipments);
    //console.log(`🗑️ Equipo removido de lista:`, removedEquipment);
  };

  // ✅ CORREGIDO: Asignar equipos usando WorksService
  const handleAssignEquipments = async () => {
    // ✅ BLOQUEO INMEDIATO
    if (processingAssignment || assignmentRef.current) {
      console.warn('⚠️ Ya se está procesando asignación, ignorando clic');
      return;
    }

    if (equipmentsToAssign.length === 0) {
      setSnackbar({
        open: true,
        type: 'error',
        message: 'No ha seleccionado ningún equipo para asignar'
      });
      return;
    }
    
    try {
      // ✅ Bloquear ANTES de operación async
      assignmentRef.current = true;
      setProcessingAssignment(true);
      
      //console.log(`🔄 Asignando ${equipmentsToAssign.length} Herramientas a obra ${workId}...`);
      
      // ✅ USAR WorksService.assignToolsToWork
      const result = await WorksService.assignToolsToWork(equipmentsToAssign, workId);
      
      if (!result.success) {
        throw new Error(result.statusText || 'Error al asignar Herramientas');
      }
      
      //console.log(`✅ Herramientas asignadas exitosamente a obra ${workId}`);
      
      setSnackbar({
        open: true,
        type: 'success',
        message: `${equipmentsToAssign.length} equipo(s) asignado(s) correctamente`
      });
      
      // Callback para actualizar la lista de equipos en el componente padre
      if (onEquipmentsAssigned) {
        onEquipmentsAssigned();
      }
      
      // Cerrar modal después de un breve delay
      setTimeout(() => {
        if (mountedRef.current) {
          onHide();
        }
      }, 1500);
      
    } catch (error) {
      console.error('❌ Error al asignar Herramientas:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al asignar Herramientas: ' + (error.message || 'Error desconocido')
      });
    } finally {
      assignmentRef.current = false;
      if (mountedRef.current) {
        setProcessingAssignment(false);
      }
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className='text-center w-100'>Asignar Herramientas a la Obra</Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {loading ? (
            <div className="text-center my-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Cargando...</span>
              </Spinner>
              <p className="mt-2">Cargando Herramientas disponibles...</p>
            </div>
          ) : (
            <>
              <Row>
                {/* Columna izquierda - Búsqueda y lista de equipos */}
                <Col md={6}>
                  <h6 className="mb-2">Herramientas en Stock</h6>
                  
                  <InputGroup className="mb-2">
                    <Form.Control
                      placeholder="Buscar por nombre o tipo..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      disabled={processingAssignment}
                    />
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                  </InputGroup>
                  
                  {/* Filtros rápidos por tipo */}
                  <div className="d-flex flex-wrap mb-2 gap-1">
                    {TIPOS_EQUIPOS.map(tipo => (
                      <Button
                        key={tipo.id}
                        variant={activeFilters[tipo.id] ? "primary" : "outline-secondary"}
                        size="sm"
                        onClick={() => handleFilterChange(tipo.id)}
                        className="mb-1"
                        disabled={processingAssignment}
                      >
                        {tipo.descripcion}
                      </Button>
                    ))}
                  </div>
                  
                  <div 
                    style={{ 
                      height: selectedEquipment ? '180px' : '320px', 
                      overflowY: 'auto', 
                      border: '1px solid #dee2e6',
                      borderRadius: '0.25rem' 
                    }}
                  >
                    <ListGroup variant="flush">
                      {filteredEquipments.length === 0 ? (
                        <ListGroup.Item className="text-center text-muted py-4">
                          No hay Herramientas disponibles que coincidan con la búsqueda
                        </ListGroup.Item>
                      ) : (
                        filteredEquipments.map(equipment => {
                          const availableStock = getAvailableStock(equipment);
                          return (
                            <ListGroup.Item 
                              key={equipment.id}
                              action
                              active={selectedEquipment && selectedEquipment.id === equipment.id}
                              onClick={() => handleSelectEquipment(equipment)}
                              style={{ cursor: 'pointer' }}
                              disabled={availableStock <= 0 || processingAssignment}
                            >
                              <div className="d-flex justify-content-between">
                                <div>
                                  {equipment.name}
                                  <Badge bg="secondary" className="ms-2">
                                    {getTipoDescripcion(equipment.type, TIPOS_EQUIPOS)}
                                  </Badge>
                                </div>
                                <div>
                                  <Badge bg={availableStock > 0 ? 'success' : 'danger'}>
                                    Stock: {availableStock}
                                  </Badge>
                                </div>
                              </div>
                            </ListGroup.Item>
                          );
                        })
                      )}
                    </ListGroup>
                  </div>
                  
                  {/* Formulario de asignación cuando hay equipo seleccionado */}
                  {selectedEquipment && (
                    <div className="mt-3 p-3 border rounded">
                      <h6>Detalles de asignación</h6>
                      
                      <Form.Group className="mb-2">
                        <Form.Label>Equipo seleccionado</Form.Label>
                        <Form.Control
                          type="text"
                          value={`${selectedEquipment.name} - ${getTipoDescripcion(selectedEquipment.type, TIPOS_EQUIPOS)}`}
                          readOnly
                        />
                      </Form.Group>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label>Cantidad</Form.Label>
                            <Form.Control
                              type="number"
                              min="1"
                              max={getAvailableStock(selectedEquipment)}
                              value={equipmentQuantity}
                              onChange={(e) => handleQuantityChange(e.target.value)}
                              disabled={processingAssignment}
                            />
                            <Form.Text>
                              Stock disponible: {getAvailableStock(selectedEquipment)}
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-2">
                            <Form.Label>
                              Seriales (opcional) - {serials.length}/{equipmentQuantity}
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="text"
                                value={serialNumber}
                                onChange={(e) => setSerialNumber(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSerial())}
                                placeholder="Ingrese y presione Enter"
                                disabled={processingAssignment || serials.length >= equipmentQuantity}
                              />
                              <Button 
                                variant="outline-secondary" 
                                onClick={handleAddSerial}
                                disabled={processingAssignment || serials.length >= equipmentQuantity || !serialNumber.trim()}
                              >
                                <FaPlus />
                              </Button>
                            </InputGroup>
                            <Form.Text>
                              {serials.length >= equipmentQuantity 
                                ? `✅ Máximo alcanzado (${equipmentQuantity})` 
                                : `${serials.length} de ${equipmentQuantity} seriales`
                              }
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      {/* Lista de seriales agregados */}
                      {serials.length > 0 && (
                        <div className="mt-2">
                          <div className="d-flex flex-wrap">
                            {serials.map((serial, index) => (
                              <Badge 
                                bg="primary" 
                                className="me-1 mb-1 p-2" 
                                key={index}
                                style={{ cursor: 'pointer' }}
                                onClick={() => !processingAssignment && handleRemoveSerial(index)}
                              >
                                {serial} <FaTimes size={10} />
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="d-flex justify-content-end mt-2">
                        <Button 
                          variant="secondary"
                          size="sm"
                          className="me-2"
                          onClick={() => setSelectedEquipment(null)}
                          disabled={processingAssignment}
                        >
                          <FaTimes className="me-1" /> Cancelar
                        </Button>
                        <Button 
                          variant="primary"
                          size="sm"
                          onClick={handleAddToAssignList}
                          disabled={processingAssignment}
                        >
                          <FaPlus className="me-1" /> Agregar
                        </Button>
                      </div>
                    </div>
                  )}
                </Col>
                
                {/* Columna derecha - Equipos a asignar */}
                <Col md={6}>
                  <h6 className="mb-2">Herramientas Para Asignar</h6>
                  
                  <div 
                    style={{ 
                      height: '320px', 
                      overflowY: 'auto', 
                      border: '1px solid #dee2e6',
                      borderRadius: '0.25rem' 
                    }}
                  >
                    <ListGroup variant="flush">
                      {equipmentsToAssign.length === 0 ? (
                        <ListGroup.Item className="text-center text-muted py-4">
                          No ha seleccionado Herramientas para asignar
                        </ListGroup.Item>
                      ) : (
                        equipmentsToAssign.map((equipment, index) => (
                          <ListGroup.Item key={`${equipment.id}-${index}`}>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <div>
                                  <strong>{equipment.name}</strong>
                                  <Badge bg="secondary" className="ms-2">
                                    {getTipoDescripcion(equipment.type, TIPOS_EQUIPOS)}
                                  </Badge>
                                </div>
                                <div className="text-muted">
                                  Cantidad: {equipment.quantity} 
                                  {equipment.serial && equipment.serial.length > 0 && (
                                    <> • Seriales: {equipment.serial.join(', ')}</>
                                  )}
                                </div>
                              </div>
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => handleRemoveFromAssignList(index)}
                                disabled={processingAssignment}
                              >
                                <FaTimes />
                              </Button>
                            </div>
                          </ListGroup.Item>
                        ))
                      )}
                    </ListGroup>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={onHide} 
            disabled={processingAssignment}
          >
            <FaTimes className="me-1" /> Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssignEquipments} 
            disabled={processingAssignment || equipmentsToAssign.length === 0}
          >
            {processingAssignment ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                Procesando...
              </>
            ) : (
              <>
                <FaSave className="me-1" /> Asignar Herramientas ({equipmentsToAssign.length})
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ✅ CustomSnackbar integrado */}
      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />
    </>
  );
};

export default AssignEquipmentModal;